import { useState, useMemo } from "react";
import { ReadOnlyPill } from "@/components/auth/ReadOnlyPill";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Bell, Plus, FileText, Pencil, Loader2, MoreHorizontal, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useLicences, useOwnersList, useRunLicenceAlerts, getLicenceDocumentUrl, useAllLicenceAlertLogs } from "@/features/compliance/api";
import { LICENCE_TYPES, licenceStatus, daysRemaining, type LicenceRow } from "@/features/compliance/types";
import { LicenceStatusBadge } from "@/features/compliance/badges";
import { ComplianceKpis, type ComplianceKpiKey } from "@/features/compliance/ComplianceKpis";
import { RenewLicenceDialog } from "@/features/compliance/RenewLicenceDialog";
import { useOptionValues } from "@/features/configuration/api";
import { useBranch } from "@/context/BranchContext";
import { DocumentViewerDialog } from "@/components/documents/DocumentViewerDialog";
import { formatDate } from "@/features/assets/format";
import { formatUserLabel } from "@/lib/utils";

function formatTypeLabel(t: string, m?: Map<string, string>) {
  if (!t) return "";
  const label = m?.get(t) || t;
  if (label === t) {
    const normalized = t.replace(/_/g, " ").toLowerCase();
    const predefined = LICENCE_TYPES.find((x) => x.toLowerCase() === normalized);
    if (predefined) return predefined;
    return t.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  }
  return label;
}

export const Route = createFileRoute("/_authenticated/compliance/licences/")({
  component: CompliancePage,
  validateSearch: (s: Record<string, unknown>) => ({ expiry: s.expiry as string | undefined }),
});

const VIEWER_ROLES = new Set(["super_admin", "biomed_head", "facility_manager", "finance", "quality", "management"]);
const EDITOR_ROLES = new Set(["super_admin", "biomed_head", "facility_manager", "quality"]);
const ALERT_ROLES = new Set(["super_admin", "biomed_head", "quality"]);

function CompliancePage() {
  const { data: me } = useCurrentUser();
  const navigate = useNavigate();
  const { currentBranchId, lockedBranchId } = useBranch();
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;
  const roles = me?.roles ?? [];
  const canView = roles.some((r) => VIEWER_ROLES.has(r));
  const canEdit = roles.some((r) => EDITOR_ROLES.has(r));
  const canAlerts = roles.some((r) => ALERT_ROLES.has(r));

  const { expiry } = Route.useSearch();
  const [type, setType] = useState<string>("all");
  const [status, setStatus] = useState<string>(expiry === "soon" ? "Expiring Soon" : "all");
  const [ownerId, setOwnerId] = useState<string>("all");
  const [docFilter, setDocFilter] = useState<"all" | "with" | "missing">("all");
  const [search, setSearch] = useState("");
  const [renewTarget, setRenewTarget] = useState<LicenceRow | null>(null);

  const { data: rows = [], isLoading } = useLicences({
    type: type === "all" ? null : type,
    ownerId: ownerId === "all" ? null : ownerId,
    search,
    documentFilter: docFilter,
    branchId: effectiveBranchId,
  });
  const { data: owners = [] } = useOwnersList();
  const { data: allAlerts = [] } = useAllLicenceAlertLogs();
  const lastAlertByLicence = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of allAlerts) if (!m.has(a.licence_id)) m.set(a.licence_id, a.sent_at);
    return m;
  }, [allAlerts]);
  const runAlerts = useRunLicenceAlerts();
  const { values: typeCfg } = useOptionValues("compliance", "licence_type");
  const typeFilterOpts: string[] = typeCfg.length ? typeCfg.map((o) => o.value) : [...LICENCE_TYPES];
  const typeMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of typeCfg) m.set(o.value, o.label);
    return m;
  }, [typeCfg]);

  if (!me) return null;
  if (!canView) {
    return <Card className="mx-auto max-w-xl"><CardContent className="space-y-2 p-6">
      <h2 className="text-lg font-semibold">Permission required</h2>
      <p className="text-sm text-muted-foreground">You do not have access to the Compliance Register.</p>
    </CardContent></Card>;
  }

  const filtered = status === "all" ? rows : rows.filter((r) => licenceStatus(r.valid_till) === status);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Statutory Licence & Compliance Register</h1> <ReadOnlyPill module="compliance" />
          <p className="text-sm text-muted-foreground">Track all hospital licences and certifications.</p>
        </div>
        <div className="flex gap-2">
          {canAlerts && (
            <Button variant="outline" disabled={runAlerts.isPending} onClick={async () => {
              try { const n = await runAlerts.mutateAsync(); toast.success(`Alert check complete · ${n} notification(s) created`); }
              catch (e) { toast.error((e as Error).message); }
            }}>
              <Bell className="mr-1 h-4 w-4" />Run Alert Check
            </Button>
          )}
          {canEdit && (
            <Button onClick={() => navigate({ to: "/compliance/licences/new" })}>
              <Plus className="mr-1 h-4 w-4" />New Licence
            </Button>
          )}
        </div>
      </div>

      <ComplianceKpis
        rows={rows}
        activeKey={
          docFilter === "missing"
            ? "Missing"
            : status === "all"
              ? null
              : (status as ComplianceKpiKey)
        }
        onSelect={(k) => {
          if (!k) { setStatus("all"); setDocFilter("all"); return; }
          if (k === "Missing") { setDocFilter("missing"); setStatus("all"); return; }
          setStatus(k);
          setDocFilter("all");
        }}
      />

      <Card><CardContent className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input className="w-56" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {typeFilterOpts.map((t) => <SelectItem key={t} value={t}>{formatTypeLabel(t, typeMap)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="Valid">Valid</SelectItem>
              <SelectItem value="Expiring Soon">Expiring Soon</SelectItem>
              <SelectItem value="Expired">Expired</SelectItem>
            </SelectContent>
          </Select>
          <Select value={ownerId} onValueChange={setOwnerId}>
            <SelectTrigger className="w-56"><SelectValue placeholder="Owner" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All owners</SelectItem>
              {owners.map((u) => <SelectItem key={u.id} value={u.id}>{formatUserLabel(u as any)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={docFilter} onValueChange={(v) => setDocFilter(v as "all" | "with" | "missing")}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Document" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All documents</SelectItem>
              <SelectItem value="with">With document</SelectItem>
              <SelectItem value="missing">Missing document</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        {isLoading ? <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div> :
        filtered.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No licences found.</div> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Licence No</TableHead>
                <TableHead>Asset / Facility</TableHead>
                <TableHead>Authority</TableHead>
                <TableHead>Valid Till</TableHead>
                <TableHead>Days</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Doc</TableHead>
                <TableHead>Last Alert</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((l) => {
                const d = daysRemaining(l.valid_till);
                const s = licenceStatus(l.valid_till);
                const disposed = l.asset?.status === "Disposed" || l.asset?.status === "Condemned";
                const lastAlert = lastAlertByLicence.get(l.id);
                return (
                  <TableRow key={l.id}>
                    <TableCell className="text-sm font-medium">{formatTypeLabel(l.type, typeMap)}</TableCell>
                    <TableCell className="font-mono text-xs">{l.licence_no}</TableCell>
                    <TableCell className="text-sm">
                      {l.asset ? (
                        <div>
                          <div className="flex items-center gap-1">
                            <Link to="/assets/$assetId" params={{ assetId: l.asset.id }} className="font-medium hover:underline">{l.asset.name}</Link>
                            {disposed && <Badge variant="destructive" className="text-[10px]">{l.asset.status}</Badge>}
                          </div>
                          <div className="font-mono text-[11px] text-muted-foreground">{l.asset.asset_code}</div>
                          {l.facility_area && <div className="text-[11px] text-muted-foreground">{l.facility_area}</div>}
                        </div>
                      ) : l.facility_area ? l.facility_area : "—"}
                    </TableCell>
                    <TableCell className="text-sm">{l.issuing_authority ?? "—"}</TableCell>
                    <TableCell className="text-sm">{l.valid_till}</TableCell>
                    <TableCell className={`text-sm tabular-nums ${d < 0 ? "text-red-600 font-semibold" : d <= 30 ? "text-amber-600 font-semibold" : ""}`}>{d}</TableCell>
                    <TableCell className="text-sm">{l.owner?.full_name || l.owner?.email || "—"}</TableCell>
                    <TableCell><LicenceStatusBadge status={s} /></TableCell>
                    <TableCell>
                      {l.document_url ? (
                        <LicenceDocumentLink path={l.document_url} />
                      ) : <Badge variant="outline" className="border-amber-500 text-amber-700">Missing</Badge>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {lastAlert ? formatDate(lastAlert) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {canEdit ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate({ to: "/compliance/licences/$licenceId/edit", params: { licenceId: l.id } })}>
                              <Pencil className="mr-2 h-3 w-3" />View / Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setRenewTarget(l)}>
                              <RefreshCw className="mr-2 h-3 w-3" />Renew
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      {renewTarget && (
        <RenewLicenceDialog
          licence={renewTarget}
          open={!!renewTarget}
          onOpenChange={(v) => { if (!v) setRenewTarget(null); }}
        />
      )}
    </div>
  );
}


function LicenceDocumentLink({ path }: { path: string }) {
  const [open, setOpen] = useState(false);
  const fileName = path.split("/").pop() ?? "licence-document";
  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        <FileText className="mr-1 h-3 w-3" />View
      </Button>
      <DocumentViewerDialog
        open={open}
        onOpenChange={setOpen}
        bucket="licence-docs"
        path={path}
        fileName={fileName}
      />
    </>
  );
}
